import ReconnectingWebSocket from 'reconnecting-websocket';

/**
 * WebSocket service with token-based authentication, heartbeat,
 * automatic token refresh on expiry, and reconnection support.
 *
 * Decoupled from tokenService — uses configurable getToken / refreshToken
 * callbacks so any auth system can plug in.
 */
class WebSocketService {
  constructor() {
    this.rws = null;
    this.listeners = new Map();
    this.isConnected = false;
    this.isRefreshingToken = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttemptsBeforeTokenRefresh = 2;
    this.heartbeatInterval = null;
    this.lastMessageTime = null;

    this._getToken = null;
    this._refreshToken = null;
    this._onTokenChange = null;
    this._unsubscribeTokenChange = null;
    this._url = null;
  }

  /**
   * Configure the service before connecting.
   * @param {object}   config
   * @param {string}   config.url            - WebSocket URL
   * @param {Function} config.getToken       - Returns current backend token
   * @param {Function} config.refreshToken   - Returns Promise<newToken>
   * @param {Function} [config.onTokenChange] - Subscribe to token changes; must return unsubscribe fn
   */
  configure({ url, getToken, refreshToken, onTokenChange }) {
    this._url = url;
    this._getToken = getToken;
    this._refreshToken = refreshToken;

    if (this._unsubscribeTokenChange) {
      this._unsubscribeTokenChange();
      this._unsubscribeTokenChange = null;
    }

    if (onTokenChange) {
      this._onTokenChange = onTokenChange;
      this._unsubscribeTokenChange = onTokenChange(() => {
        if (this.isRefreshingToken) return;
        if (!this.rws) return;

        const recentlyReceivedMessage =
          this.lastMessageTime && Date.now() - this.lastMessageTime < 5000;

        if (recentlyReceivedMessage) return;

        this.reconnect();
      });
    }
  }

  isTokenExpiredMessage(data) {
    if (!data || typeof data !== 'object') return false;

    const message = data.message || data.error || data.reason || '';
    const messageStr = typeof message === 'string' ? message.toLowerCase() : '';

    return (
      (messageStr.includes('token') &&
        (messageStr.includes('expired') ||
          messageStr.includes('invalid') ||
          messageStr.includes('unauthorized'))) ||
      data.code === 401 ||
      data.code === 403
    );
  }

  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.isReady()) {
        try {
          this.rws.send(JSON.stringify({ type: 'ping' }));
        } catch (error) {
          console.error('[WebSocket] Error sending heartbeat', error);
        }
      } else {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    }, 30_000);
  }

  async handleTokenRefresh() {
    if (this.isRefreshingToken || !this._refreshToken) return;

    this.isRefreshingToken = true;

    try {
      await this._refreshToken();

      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }

      if (this.rws) {
        this.rws.removeEventListener('open', this.handleOpen);
        this.rws.removeEventListener('message', this.handleMessage);
        this.rws.removeEventListener('error', this.handleError);
        this.rws.removeEventListener('close', this.handleClose);
        this.rws.close();
        this.rws = null;
      }

      this.isConnected = false;
      this.isRefreshingToken = false;
      this.reconnectAttempts = 0;

      this.connect();
    } catch (error) {
      console.error('[WebSocket] Failed to refresh token', error);
      this.isRefreshingToken = false;
      this.emit('tokenRefreshError', error);
    }
  }

  handleOpen = () => {
    this.isConnected = true;
    this.reconnectAttempts = 0;

    const token = this._getToken?.();
    if (token) {
      try {
        this.rws.send(
          JSON.stringify({
            type: 'authorization',
            Authorization: token,
          }),
        );
      } catch (err) {
        console.warn('[WebSocket] Could not send auth message:', err);
      }
    }

    this.startHeartbeat();
    this.emit('open', null);
  };

  handleMessage = (event) => {
    this.lastMessageTime = Date.now();
    try {
      const data = JSON.parse(event.data);

      if (this.isTokenExpiredMessage(data)) {
        console.warn('[WebSocket] Server indicates token expired');
        this.handleTokenRefresh();
        return;
      }

      this.emit('message', data);
    } catch {
      this.emit('message', event.data);
    }
  };

  handleError = (error) => {
    console.error('[WebSocket] Error occurred', error);
    this.emit('error', error);
  };

  handleClose = (event) => {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (event.code === 1008) {
      const reason = (event.reason || '').toLowerCase();
      if (
        reason.includes('token') ||
        reason.includes('expired') ||
        reason.includes('unauthorized') ||
        !this.isRefreshingToken
      ) {
        this.handleTokenRefresh();
        return;
      }
    }

    if (event.code === 1006) {
      this.reconnectAttempts++;
      if (
        this.reconnectAttempts >= this.maxReconnectAttemptsBeforeTokenRefresh &&
        !this.isRefreshingToken
      ) {
        this.reconnectAttempts = 0;
        this.handleTokenRefresh();
        return;
      }
    } else {
      this.reconnectAttempts = 0;
    }

    this.isConnected = false;
    this.emit('close', event);
  };

  connect() {
    if (this.isRefreshingToken) return;
    if (this.rws && this.rws.readyState === WebSocket.OPEN) return;
    if (!this._url || !this._getToken) {
      console.warn('[WebSocket] Not configured — call configure() before connect()');
      return;
    }

    const token = this._getToken();
    if (!token) {
      console.warn('[WebSocket] No token available, cannot connect');
      return;
    }

    try {
      const urlProvider = () => {
        const currentToken = this._getToken?.();
        return `${this._url}?Authorization=${encodeURIComponent(currentToken)}`;
      };

      this.rws = new ReconnectingWebSocket(urlProvider, [], {
        maxRetries: 10,
        maxReconnectionDelay: 10000,
        minReconnectionDelay: 1000,
        reconnectionDelayGrowFactor: 1.3,
        connectionTimeout: 4000,
        debug: false,
      });

      this.rws.addEventListener('open', this.handleOpen);
      this.rws.addEventListener('message', this.handleMessage);
      this.rws.addEventListener('error', this.handleError);
      this.rws.addEventListener('close', this.handleClose);
    } catch (error) {
      console.error('[WebSocket] Failed to create connection', error);
      this.emit('error', error);
    }
  }

  disconnect() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.rws) {
      this.rws.removeEventListener('open', this.handleOpen);
      this.rws.removeEventListener('message', this.handleMessage);
      this.rws.removeEventListener('error', this.handleError);
      this.rws.removeEventListener('close', this.handleClose);
      this.rws.close();
      this.rws = null;
    }
    this.isConnected = false;
    this.isRefreshingToken = false;
    this.reconnectAttempts = 0;
  }

  reconnect() {
    if (this.isRefreshingToken) return;
    this.disconnect();
    this.connect();
  }

  send(data) {
    if (!this.rws || this.rws.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot send, connection not open');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.rws.send(message);
      return true;
    } catch (error) {
      console.error('[WebSocket] Error sending message', error);
      return false;
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) callbacks.splice(index, 1);
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    this.listeners.get(event).forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[WebSocket] Error in listener for ${event}`, error);
      }
    });
  }

  getState() {
    if (!this.rws) return 'CLOSED';
    switch (this.rws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }

  isReady() {
    return this.isConnected && this.rws && this.rws.readyState === WebSocket.OPEN;
  }
}

export default WebSocketService;
